"use client"

import { HelpCircle } from "@untitledui/icons"
import type { ReactNode } from "react"

import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip"

import type { Help } from "./help-copy"

type Placement = "top" | "left" | "bottom" | "right"

/**
 * The hover card body: what the control is, then the two directions as
 * "Decrease to …" / "Increase to …" and any interaction as "Pairs with".
 */
function HelpBody({ help }: { help: Help }) {
  const rows: [string, string | undefined][] = [
    ["Decrease to", help.decrease],
    ["Increase to", help.increase],
    ["Pairs with", help.pairs],
  ]
  return (
    <span className="flex flex-col gap-1.5">
      <span>{help.what}</span>
      {rows.some(([, v]) => v) && (
        <span className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
          {rows.map(
            ([k, v]) =>
              v && (
                <span key={k} className="contents">
                  <span className="text-[11px] font-semibold whitespace-nowrap text-white">
                    {k}
                  </span>
                  <span>{v}</span>
                </span>
              ),
          )}
        </span>
      )}
    </span>
  )
}

interface HelpTipProps {
  /** The control's own label; becomes the card's title. */
  label: string
  help: Help
  placement?: Placement
}

/** A small help glyph that opens the hover card on hover or keyboard focus. */
export function HelpTip({ label, help, placement = "left" }: HelpTipProps) {
  return (
    <Tooltip
      title={label}
      description={<HelpBody help={help} />}
      placement={placement}
      arrow
      delay={150}
    >
      <TooltipTrigger
        aria-label={`About ${label}`}
        className="text-fg-quaternary hover:text-fg-quaternary_hover focus-visible:text-fg-quaternary_hover outline-focus-ring rounded-full transition duration-150 focus-visible:outline-2 focus-visible:outline-offset-1"
      >
        <HelpCircle className="size-3.5" />
      </TooltipTrigger>
    </Tooltip>
  )
}

interface HelpHeadingProps {
  title: string
  help?: Help
  hint?: string
  placement?: Placement
  size?: "sm" | "xs"
  children?: ReactNode
}

/** A section heading with its help glyph and optional trailing content. */
export function HelpHeading({
  title,
  help,
  hint,
  placement,
  size = "sm",
  children,
}: HelpHeadingProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <h2
        className={[
          "text-secondary font-semibold",
          size === "xs" ? "text-xs" : "text-sm",
        ].join(" ")}
      >
        {title}
      </h2>
      {help && <HelpTip label={title} help={help} placement={placement} />}
      {hint && <p className="text-tertiary text-xs">{hint}</p>}
      {children}
    </div>
  )
}
